<script setup lang="ts">
import type { Broker, BrokerLocationReviews, BrokerProfile } from '~~/types/Broker'

const props = withDefaults(defineProps<{
  broker: Broker
  brokerProfile?: BrokerProfile | null
  locationReviews?: BrokerLocationReviews[]
}>(), {
  brokerProfile: null,
  locationReviews: () => [],
})

// ============================================
// COMPUTED PROPERTIES
// ============================================

const topLocations = computed(() => props.broker?.top_5_locations || [])

const hasLocations = computed(() => topLocations.value.length > 0)

const hasMultipleLocations = computed(() => topLocations.value.length >= 5)

const propertyTypes = computed(() => props.broker?.property_types_sold || [])

const hasPropertyTypes = computed(() => propertyTypes.value.length > 0)

const topPropertyType = computed(() => propertyTypes.value[0]?.property_category || 'woningen')

const hasRating = computed(() => {
  return props.brokerProfile?.avg_rating != null && props.brokerProfile.avg_rating > 0
})

const formattedRating = computed(() => {
  if (!props.brokerProfile?.avg_rating) return null
  return props.brokerProfile.avg_rating.toFixed(1)
})

const hasWebsiteSummary = computed(() => {
  return props.brokerProfile?.website_summary && props.brokerProfile.website_summary.trim().length > 0
})

const websiteSummaryParagraphs = computed(() => {
  if (!props.brokerProfile?.website_summary) return []
  return props.brokerProfile.website_summary
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(p => p.length > 0)
})

// Zonder websitetekst tonen we wat de verkoopcijfers wel laten zien
const fallbackSummary = computed(() => {
  const { broker_name: name, houses_sold_last_12_months: sold, price_comparison: comparison, price_segment: segment, city_name: city } = props.broker
  const places = topLocations.value.slice(0, 3).map(l => l.name)
  const sentences: string[] = []
  if (sold > 0) {
    const where = places.length ? `, vooral in ${places.join(', ').replace(/, ([^,]*)$/, ' en $1')}` : ''
    sentences.push(`${name} verkocht de afgelopen 12 maanden ${sold} ${sold === 1 ? 'woning' : 'woningen'}${where}.`)
  }
  if (propertyTypes.value[0]?.property_category) {
    sentences.push(`De meeste verkopen zijn van het type ${topPropertyType.value.toLowerCase()}.`)
  }
  if (comparison && segment) {
    sentences.push(`Het kantoor verkoopt woningen die in een ${comparison} vallen ten opzichte van andere woningen in ${city || 'de regio'} en omgeving, en is actief in het ${segment}.`)
  }
  return sentences.join(' ')
})

const hasReviewSummary = computed(() => {
  return props.brokerProfile?.review_summary && props.brokerProfile.review_summary.trim().length > 0
})

const reviewSummaryParagraphs = computed(() => {
  if (!props.brokerProfile?.review_summary) return []
  return props.brokerProfile.review_summary
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(p => p.length > 0)
})

const hasPositiveTags = computed(() => (props.brokerProfile?.positive_tags?.length ?? 0) > 0)
const hasNegativeTags = computed(() => (props.brokerProfile?.negative_tags?.length ?? 0) > 0)
const hasTags = computed(() => hasPositiveTags.value || hasNegativeTags.value)

const hasEmployees = computed(() => (props.brokerProfile?.employees?.length ?? 0) > 0)

const hasRatingsDistribution = computed(() => {
  if (!props.brokerProfile?.ratings_distribution) return false
  return Object.keys(props.brokerProfile.ratings_distribution).length > 0
})

const ratingsDistributionItems = computed(() => {
  if (!props.brokerProfile?.ratings_distribution) return []
  const dist = props.brokerProfile.ratings_distribution
  const total = Object.values(dist).reduce((sum, count) => sum + count, 0)
  if (total === 0) return []

  return [5, 4, 3, 2, 1].map(star => ({
    star,
    count: dist[String(star)] ?? 0,
    percentage: total > 0 ? ((dist[String(star)] ?? 0) / total) * 100 : 0,
  }))
})

const hasLocationReviews = computed(() => {
  return props.brokerProfile?.locations && props.brokerProfile.locations.length > 1
})

const hasSpecializations = computed(() => (props.brokerProfile?.specializations?.length ?? 0) > 0)

const hasTopReviews = computed(() => (props.brokerProfile?.top_reviews?.length ?? 0) > 0)

const formatReviewDate = (date: string) =>
  new Date(date).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })

const lastReviewDateLabel = computed(() => {
  const date = props.brokerProfile?.last_review_date
  return date ? formatReviewDate(date) : null
})

const hasLocationReviewAccordion = computed(() => props.locationReviews.length > 0)

const locationReviewAccordionItems = computed(() =>
  props.locationReviews.map(location => ({
    label: `${location.title} (${location.reviews.length} ${location.reviews.length === 1 ? 'review' : 'reviews'})`,
    reviews: location.reviews,
  })),
)

const hasCosts = computed(() => props.broker.avg_house_price_raw > 0)

const salesSentence = computed(() => {
  const { total_houses_sold: total, houses_sold_last_12_months: recent } = props.broker
  const recentPart = recent && recent < total ? `, waarvan ${recent} in de afgelopen 12 maanden` : ''
  return `dat in totaal ${total} woningen verkocht${recentPart}.`
})

// ============================================
// MARKET CONTEXT
// ============================================

const hasCityContext = computed(() => props.broker.city_median_price != null && props.broker.city_median_price > 0)

const cityComparison = computed(() => {
  const b = props.broker
  if (!hasCityContext.value) return null

  const priceDiff = b.city_median_price
    ? Math.round(((b.avg_house_price_raw - b.city_median_price) / b.city_median_price) * 100)
    : null

  const daysDiff = b.city_median_days_on_market != null
    ? b.city_median_days_on_market - b.avg_days_on_market
    : null

  const marketShare = b.city_total_houses && b.city_total_houses > 0
    ? ((b.total_houses_sold / b.city_total_houses) * 100).toFixed(1)
    : null

  return { priceDiff, daysDiff, marketShare }
})

// ============================================
// FAQ GENERATION
// ============================================

const faqs = computed(() => {
  const questions = []

  if (props.broker?.total_houses_sold != null) {
    questions.push({
      label: `Hoeveel woningen heeft ${props.broker.broker_name} verkocht?`,
      content: `${props.broker.broker_name} heeft in totaal ${props.broker.total_houses_sold} woningen verkocht.${props.broker.houses_sold_last_12_months ? ` In de afgelopen 12 maanden waren dit ${props.broker.houses_sold_last_12_months} woningen.` : ''}`,
    })
  }

  if (props.broker?.avg_house_price) {
    questions.push({
      label: `Wat is de gemiddelde verkoopprijs bij ${props.broker.broker_name}?`,
      content: `De gemiddelde verkoopprijs van woningen verkocht door ${props.broker.broker_name} is ${props.broker.avg_house_price}.${props.broker.median_price ? ` De mediane verkoopprijs ligt op ${props.broker.median_price}.` : ''}`,
    })
  }

  if (props.broker?.avg_days_on_market != null && props.broker.avg_days_on_market > 0) {
    questions.push({
      label: `Hoe snel verkoopt ${props.broker.broker_name} een woning?`,
      content: `Woningen verkocht door ${props.broker.broker_name} staan gemiddeld ${props.broker.avg_days_on_market} dagen op de markt voordat ze verkocht worden.`,
    })
  }

  if (hasCosts.value) {
    questions.push({
      label: `Wat kost ${props.broker.broker_name}?`,
      content: `Bij een gangbare courtage van 1% tot 1,3% en de gemiddelde verkoopprijs van ${props.broker.avg_house_price} kost verkoopbegeleiding door ${props.broker.broker_name} naar schatting ${props.broker.broker_cost_min} tot ${props.broker.broker_cost_max}. Aankoopbegeleiding kost ${props.broker.buying_agent_min} tot ${props.broker.buying_agent_max}. Vraag een offerte op voor het exacte tarief.`,
    })
  }

  if (hasLocations.value) {
    questions.push({
      label: `In welke plaatsen is ${props.broker.broker_name} actief?`,
      content: `${props.broker.broker_name} is voornamelijk actief in ${topLocations.value.map(l => l.name).join(', ')}.`,
    })
  }
  else if (props.broker?.city_name) {
    questions.push({
      label: `In welke plaatsen is ${props.broker.broker_name} actief?`,
      content: `${props.broker.broker_name} is actief in ${props.broker.city_name} en omgeving.`,
    })
  }

  // Review-based FAQ questions
  if (hasRating.value && props.brokerProfile?.total_reviews) {
    questions.push({
      label: `Wat is de beoordeling van ${props.broker.broker_name}?`,
      content: `${props.broker.broker_name} heeft een gemiddelde beoordeling van ${formattedRating.value} sterren op basis van ${props.brokerProfile.total_reviews} reviews.`,
    })
  }

  if (hasSpecializations.value) {
    questions.push({
      label: `Waarin is ${props.broker.broker_name} gespecialiseerd?`,
      content: `${props.broker.broker_name} is gespecialiseerd in ${props.brokerProfile!.specializations.join(', ')}.`,
    })
  }

  if (props.brokerProfile?.typical_client) {
    questions.push({
      label: `Voor wie is ${props.broker.broker_name} geschikt?`,
      content: props.brokerProfile.typical_client,
    })
  }

  if (hasEmployees.value) {
    const names = props.brokerProfile!.employees.map(e => e.name).join(', ')
    questions.push({
      label: `Wie werken er bij ${props.broker.broker_name}?`,
      content: `Bij ${props.broker.broker_name} werken onder andere ${names}.`,
    })
  }

  return questions
})

const hasFaqs = computed(() => faqs.value.length > 0)

const tipCards = [
  { icon: 'i-lucide-banknote', title: 'Kosten verkoopmakelaar', description: 'Wat kost een verkoopmakelaar?', to: '/tip/kosten-verkoopmakelaar/' },
  { icon: 'i-lucide-home', title: 'Kosten aankoopmakelaar', description: 'Tarieven en prijzen vergelijken', to: '/tip/kosten-aankoopmakelaar/' },
  { icon: 'i-lucide-git-compare-arrows', title: 'Makelaars vergelijken', description: 'Vind de beste makelaar voor jouw situatie', to: '/tip/makelaars-vergelijken/' },
  { icon: 'i-lucide-clipboard-list', title: 'Taken aankoopmakelaar', description: 'Wat doet een aankoopmakelaar precies?', to: '/tip/taken-aankoopmakelaar/' },
]
</script>

<template>
  <div class="prose max-w-none">
    <ProseP>
      {{ broker.broker_name }} is een makelaarskantoor
      <template v-if="broker.city_name">
        in <ProseA :to="`/makelaars/${broker.province_slug}/${broker.city_slug}/`">
          {{ broker.city_name }}
        </ProseA>
      </template>
      {{ salesSentence }}
      Het kantoor is vooral actief in het {{ broker.price_segment }}, met een mediane verkoopprijs van {{ broker.median_price }}.
    </ProseP>

    <!-- Statistieken -->
    <div class="not-prose my-6 rounded-xl border border-default overflow-hidden">
      <div class="bg-muted px-5 sm:px-6 py-6 sm:py-8">
        <p class="text-sm font-medium text-muted mb-1">
          {{ broker.broker_name }}
        </p>
        <div class="text-3xl sm:text-5xl font-bold tracking-[-0.02em] text-highlighted tabular-nums">
          {{ broker.total_houses_sold }} <span class="text-lg sm:text-xl font-semibold text-muted">verkocht</span>
        </div>
        <p
          v-if="broker.houses_sold_last_12_months"
          class="text-sm text-muted mt-1"
        >
          waarvan {{ broker.houses_sold_last_12_months }} in de afgelopen 12 maanden
        </p>
      </div>

      <dl class="grid grid-cols-3 divide-x divide-default border-t border-default">
        <div
          v-if="broker.avg_house_price"
          class="px-2 sm:px-4 py-3 sm:py-4 text-center"
        >
          <dd class="text-base sm:text-2xl font-bold tracking-tight text-highlighted tabular-nums">
            {{ broker.avg_house_price }}
          </dd>
          <dt class="text-[11px] sm:text-xs text-muted mt-0.5 sm:mt-1">
            Gem. verkoopprijs
          </dt>
        </div>
        <div
          v-if="broker.avg_days_on_market != null && broker.avg_days_on_market > 0"
          class="px-2 sm:px-4 py-3 sm:py-4 text-center"
        >
          <dd class="text-base sm:text-2xl font-bold tracking-tight text-highlighted tabular-nums">
            {{ broker.avg_days_on_market }} dgn
          </dd>
          <dt class="text-[11px] sm:text-xs text-muted mt-0.5 sm:mt-1">
            Gem. verkooptijd
          </dt>
        </div>
        <div
          v-if="hasRating"
          class="px-2 sm:px-4 py-3 sm:py-4 text-center"
        >
          <dd class="flex items-center justify-center gap-1">
            <UIcon
              name="i-lucide-star"
              class="size-4 sm:size-5 text-warning"
            />
            <span class="text-base sm:text-2xl font-bold tracking-tight text-highlighted tabular-nums">
              {{ formattedRating }}
            </span>
          </dd>
          <dt class="text-[11px] sm:text-xs text-muted mt-0.5 sm:mt-1">
            {{ brokerProfile?.total_reviews ?? 0 }} reviews
          </dt>
        </div>
        <div
          v-else-if="broker.median_price"
          class="px-2 sm:px-4 py-3 sm:py-4 text-center"
        >
          <dd class="text-base sm:text-2xl font-bold tracking-tight text-highlighted tabular-nums">
            {{ broker.median_price }}
          </dd>
          <dt class="text-[11px] sm:text-xs text-muted mt-0.5 sm:mt-1">
            Mediane prijs
          </dt>
        </div>
      </dl>
    </div>

    <!-- Website summary: "Over [broker]" -->
    <UCard
      v-if="hasWebsiteSummary"
      variant="subtle"
    >
      <ProseStrong>Over {{ broker.broker_name }}</ProseStrong>
      <ProseP
        v-for="(paragraph, index) in websiteSummaryParagraphs"
        :key="index"
      >
        {{ paragraph }}
      </ProseP>
    </UCard>

    <UCard
      v-else-if="fallbackSummary"
      variant="subtle"
    >
      <ProseStrong>Over {{ broker.broker_name }}</ProseStrong>
      <ProseP>{{ fallbackSummary }}</ProseP>
    </UCard>

    <!-- Review summary: "Dit zeggen klanten" -->
    <template v-if="hasReviewSummary">
      <ProseH2>Dit zeggen klanten over {{ broker.broker_name }}</ProseH2>

      <p
        v-if="lastReviewDateLabel"
        class="not-prose text-sm text-muted mb-4"
      >
        {{ brokerProfile?.total_reviews ?? 0 }} Google-reviews · laatste review {{ lastReviewDateLabel }}
      </p>

      <!-- Rating distribution -->
      <div
        v-if="hasRatingsDistribution"
        class="not-prose my-4 flex items-center gap-6 rounded-xl border border-default px-5 py-4"
      >
        <div class="flex flex-col items-center gap-1 shrink-0">
          <div class="flex items-center gap-1.5">
            <UIcon
              name="i-lucide-star"
              class="size-5 text-warning"
            />
            <span class="text-2xl font-bold text-highlighted">{{ formattedRating }}</span>
          </div>
          <span class="text-xs text-muted">
            {{ brokerProfile?.total_reviews ?? 0 }} reviews
          </span>
        </div>
        <div class="flex-1 space-y-1">
          <div
            v-for="item in ratingsDistributionItems"
            :key="item.star"
            class="flex items-center gap-1.5 text-xs"
          >
            <span class="w-3 text-right text-muted">{{ item.star }}</span>
            <div class="flex-1 h-1.5 rounded-lg bg-elevated overflow-hidden">
              <div
                class="h-full rounded-lg bg-warning"
                :style="{ width: `${item.percentage}%` }"
              />
            </div>
            <span class="w-5 text-right text-dimmed tabular-nums">{{ item.count }}</span>
          </div>
        </div>
      </div>

      <ProseP
        v-for="(paragraph, index) in reviewSummaryParagraphs"
        :key="`review-${index}`"
      >
        {{ paragraph }}
      </ProseP>

      <!-- Positive & negative tags -->
      <div
        v-if="hasTags"
        class="not-prose flex flex-wrap gap-2 my-4"
      >
        <UBadge
          v-for="tag in brokerProfile?.positive_tags"
          :key="`pos-${tag}`"
          color="success"
          variant="subtle"
        >
          <UIcon
            name="i-lucide-thumbs-up"
            class="size-3 mr-1"
          />
          {{ tag }}
        </UBadge>
        <UBadge
          v-for="tag in brokerProfile?.negative_tags"
          :key="`neg-${tag}`"
          color="neutral"
          variant="subtle"
        >
          <UIcon
            name="i-lucide-info"
            class="size-3 mr-1"
          />
          {{ tag }}
        </UBadge>
      </div>

      <!-- Top reviews: horizontaal scrollen op mobiel, gestapeld op desktop -->
      <div
        v-if="hasTopReviews"
        class="not-prose my-4 -mx-4 sm:mx-0"
      >
        <div class="flex sm:flex-col gap-3 overflow-x-auto sm:overflow-visible px-4 sm:px-0 pb-2 sm:pb-0 snap-x snap-mandatory sm:snap-none scrollbar-hide">
          <div
            v-for="(review, index) in brokerProfile?.top_reviews"
            :key="index"
            class="min-w-[280px] max-w-[320px] sm:min-w-0 sm:max-w-none shrink-0 sm:shrink snap-start rounded-xl border border-default p-4 flex flex-col"
          >
            <p class="text-sm text-highlighted line-clamp-4 flex-1">
              "{{ review.review_text }}"
            </p>
            <div class="flex items-center gap-2 mt-3 pt-3 border-t border-default">
              <div class="flex">
                <UIcon
                  v-for="star in review.rating"
                  :key="star"
                  name="i-lucide-star"
                  class="size-3 text-warning"
                />
              </div>
              <span class="text-xs text-muted">{{ review.reviewer_name }}</span>
              <span
                v-if="review.review_date"
                class="text-xs text-dimmed ml-auto"
              >{{ formatReviewDate(review.review_date) }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Employees -->
    <template v-if="hasEmployees">
      <ProseH2>Medewerkers van {{ broker.broker_name }}</ProseH2>

      <div class="not-prose my-4">
        <div class="flex sm:grid sm:grid-cols-2 gap-3 overflow-x-auto sm:overflow-visible pb-2 sm:pb-0 snap-x snap-mandatory sm:snap-none scrollbar-hide">
          <div
            v-for="employee in brokerProfile?.employees"
            :key="employee.name"
            class="w-[75vw] max-w-[75vw] sm:w-auto sm:max-w-none sm:min-w-0 shrink-0 sm:shrink snap-start rounded-xl border border-default p-4"
          >
            <p class="font-medium text-sm text-highlighted">
              {{ employee.name }}
            </p>
            <p class="text-xs text-muted mt-0.5 text-pretty">
              {{ employee.highlight }}
            </p>
          </div>
        </div>
      </div>
    </template>

    <template v-if="hasLocations">
      <ProseH2>Top plaatsen waar {{ broker.broker_name }} verkoopt</ProseH2>

      <ProseP v-if="hasMultipleLocations">
        {{ broker.broker_name }} is het meest actief in
        <ProseA :to="`/makelaars/${topLocations[0]?.province_slug}/${topLocations[0]?.city_slug}/`">
          {{ topLocations[0]?.name }}
        </ProseA>.
        Dit makelaarskantoor verkoopt verder ook in andere plaatsen als
        <ProseA :to="`/makelaars/${topLocations[1]?.province_slug}/${topLocations[1]?.city_slug}/`">
          {{ topLocations[1]?.name }}
        </ProseA>,
        <ProseA :to="`/makelaars/${topLocations[2]?.province_slug}/${topLocations[2]?.city_slug}/`">
          {{ topLocations[2]?.name }}
        </ProseA>,
        <ProseA :to="`/makelaars/${topLocations[3]?.province_slug}/${topLocations[3]?.city_slug}/`">
          {{ topLocations[3]?.name }}
        </ProseA>
        en
        <ProseA :to="`/makelaars/${topLocations[4]?.province_slug}/${topLocations[4]?.city_slug}/`">
          {{ topLocations[4]?.name }}
        </ProseA>.
      </ProseP>

      <ProseP v-else>
        {{ broker.broker_name }} is voornamelijk actief in
        <template
          v-for="(location, index) in topLocations"
          :key="location.city_slug"
        >
          <ProseA :to="`/makelaars/${location.province_slug}/${location.city_slug}/`">
            {{ location.name }}
          </ProseA>
          <template v-if="index < topLocations.length - 1">
            ,
          </template>
        </template>.
      </ProseP>

      <BrokerLocations />
    </template>

    <template v-if="hasPropertyTypes">
      <ProseH2>Type woningen die {{ broker.broker_name }} verkoopt</ProseH2>

      <ProseP>
        Naast het prijssegment waar een makelaar actief is, is het type woning van belang.
        Iedere makelaar verkoopt meer of minder van een specifiek woningtype.
        Het woningtype dat {{ broker.broker_name }} het meest verkoopt is een {{ topPropertyType }}.
      </ProseP>

      <BrokerPropertyTypes />
    </template>

    <!-- Market context comparison -->
    <template v-if="hasCityContext && cityComparison">
      <ProseH2>Hoe presteert {{ broker.broker_name }} in {{ broker.city_name }}?</ProseH2>

      <ProseP>
        In {{ broker.city_name }} verkochten {{ broker.city_active_brokers }} makelaars samen {{ broker.city_total_houses }} woningen het afgelopen jaar.
        Hieronder zie je hoe {{ broker.broker_name }} zich verhoudt tot het gemiddelde.
      </ProseP>

      <div class="not-prose my-6 rounded-xl border border-default overflow-hidden divide-y divide-default">
        <div
          v-if="cityComparison.priceDiff != null"
          class="flex items-center justify-between px-5 py-3.5"
        >
          <div class="flex items-center gap-3">
            <div
              class="flex items-center justify-center size-8 rounded-lg shrink-0"
              :class="cityComparison.priceDiff <= 5 ? 'bg-success/10' : 'bg-muted'"
            >
              <UIcon
                name="i-lucide-banknote"
                class="size-4"
                :class="cityComparison.priceDiff <= 5 ? 'text-success' : 'text-muted'"
              />
            </div>
            <div>
              <p class="text-sm font-medium text-highlighted">
                Verkoopprijs
              </p>
              <p class="text-xs text-muted">
                {{ Math.abs(cityComparison.priceDiff) }}% {{ cityComparison.priceDiff >= 0 ? 'boven' : 'onder' }} het stadsgemiddelde
              </p>
            </div>
          </div>
          <span
            class="text-sm font-semibold tabular-nums"
            :class="cityComparison.priceDiff <= 5 ? 'text-success' : 'text-highlighted'"
          >
            <template v-if="cityComparison.priceDiff > 0">+</template>{{ cityComparison.priceDiff }}%
          </span>
        </div>

        <div
          v-if="cityComparison.daysDiff != null"
          class="flex items-center justify-between px-5 py-3.5"
        >
          <div class="flex items-center gap-3">
            <div
              class="flex items-center justify-center size-8 rounded-lg shrink-0"
              :class="cityComparison.daysDiff > 0 ? 'bg-success/10' : cityComparison.daysDiff === 0 ? 'bg-muted' : 'bg-warning/10'"
            >
              <UIcon
                name="i-lucide-clock"
                class="size-4"
                :class="cityComparison.daysDiff > 0 ? 'text-success' : cityComparison.daysDiff === 0 ? 'text-muted' : 'text-warning'"
              />
            </div>
            <div>
              <p class="text-sm font-medium text-highlighted">
                Verkoopsnelheid
              </p>
              <p class="text-xs text-muted">
                {{ Math.abs(cityComparison.daysDiff) }} dagen {{ cityComparison.daysDiff > 0 ? 'sneller' : cityComparison.daysDiff === 0 ? 'gelijk aan' : 'langzamer dan' }} gemiddeld
              </p>
            </div>
          </div>
          <span
            class="text-sm font-semibold tabular-nums"
            :class="cityComparison.daysDiff > 0 ? 'text-success' : cityComparison.daysDiff === 0 ? 'text-muted' : 'text-warning'"
          >
            {{ Math.abs(cityComparison.daysDiff) }} dgn
          </span>
        </div>

        <div
          v-if="cityComparison.marketShare"
          class="flex items-center justify-between px-5 py-3.5"
        >
          <div class="flex items-center gap-3">
            <div
              class="flex items-center justify-center size-8 rounded-lg shrink-0"
              :class="Number(cityComparison.marketShare) >= 3 ? 'bg-success/10' : 'bg-muted'"
            >
              <UIcon
                name="i-lucide-pie-chart"
                class="size-4"
                :class="Number(cityComparison.marketShare) >= 3 ? 'text-success' : 'text-muted'"
              />
            </div>
            <div>
              <p class="text-sm font-medium text-highlighted">
                Marktaandeel
              </p>
              <p class="text-xs text-muted">
                {{ broker.total_houses_sold }} van {{ broker.city_total_houses }} verkopen in {{ broker.city_name }}
              </p>
            </div>
          </div>
          <span
            class="text-sm font-semibold tabular-nums"
            :class="Number(cityComparison.marketShare) >= 3 ? 'text-success' : 'text-highlighted'"
          >
            {{ cityComparison.marketShare }}%
          </span>
        </div>
      </div>
    </template>

    <template v-if="hasCosts">
      <ProseH2>Tarieven van {{ broker.broker_name }}</ProseH2>

      <ProseP>
        {{ broker.broker_name }} publiceert geen vaste tarieven. Makelaars rekenen meestal een courtage van 1% tot 1,3% van de verkoopprijs.
        Bij de gemiddelde verkoopprijs van {{ broker.avg_house_price }} komt dat neer op
        <ProseStrong>{{ broker.broker_cost_min }}</ProseStrong> tot <ProseStrong>{{ broker.broker_cost_max }}</ProseStrong> voor verkoopbegeleiding
        en {{ broker.buying_agent_min }} tot {{ broker.buying_agent_max }} voor aankoopbegeleiding.
        Vraag altijd een offerte op: een vast tarief of een lagere courtage met bonus bij een hogere opbrengst komt vaak voor.
        Lees meer over <ProseA to="/tip/kosten-verkoopmakelaar/">
          de kosten van een verkoopmakelaar
        </ProseA>.
      </ProseP>
    </template>

    <template v-if="hasFaqs">
      <ProseH2>Veelgestelde vragen over {{ broker.broker_name }}</ProseH2>

      <Faq
        :items="faqs"
        class="not-prose"
      />
    </template>

    <template v-if="hasLocationReviews || hasLocationReviewAccordion">
      <ProseH2>Reviews per vestiging</ProseH2>

      <ProseP v-if="hasLocationReviews">
        {{ broker.broker_name }} heeft {{ brokerProfile?.locations?.length }} vestigingen met reviews.
        Hieronder vind je de beoordelingen per vestiging.
      </ProseP>

      <UCard
        v-if="hasLocationReviews"
        class="not-prose my-6"
      >
        <div class="space-y-3">
          <div
            v-for="location in brokerProfile?.locations"
            :key="location.name"
            class="flex justify-between items-center py-2 border-b border-default last:border-0"
          >
            <div class="flex-1 min-w-0">
              <span class="text-sm font-medium text-highlighted block truncate">{{ location.name }}</span>
              <span
                v-if="location.address"
                class="text-xs text-muted block truncate"
              >{{ location.address }}</span>
            </div>
            <div class="flex items-center gap-2 ml-4 flex-shrink-0">
              <UIcon
                name="i-lucide-star"
                class="size-4 text-warning"
              />
              <span class="text-sm font-medium text-highlighted">{{ location.rating?.toFixed(1) ?? '-' }}</span>
              <span
                v-if="location.review_count"
                class="text-xs text-muted"
              >({{ location.review_count }})</span>
            </div>
          </div>
        </div>
      </UCard>

      <UAccordion
        v-if="hasLocationReviewAccordion"
        :items="locationReviewAccordionItems"
        :unmount-on-hide="false"
        class="not-prose my-6"
      >
        <template #body="{ item }">
          <div class="space-y-3 pb-3.5">
            <div
              v-for="(review, index) in item.reviews"
              :key="index"
              class="rounded-xl border border-default p-4"
            >
              <p class="text-sm text-highlighted">
                "{{ review.review_text }}"
              </p>
              <div class="flex items-center gap-2 mt-3 pt-3 border-t border-default">
                <div class="flex">
                  <UIcon
                    v-for="star in review.rating"
                    :key="star"
                    name="i-lucide-star"
                    class="size-3 text-warning"
                  />
                </div>
                <span class="text-xs text-muted">{{ review.reviewer_name }}</span>
                <span
                  v-if="review.review_date"
                  class="text-xs text-dimmed ml-auto"
                >{{ formatReviewDate(review.review_date) }}</span>
              </div>
            </div>
          </div>
        </template>
      </UAccordion>
    </template>
    <ProseH2>Tips bij het kiezen van een makelaar</ProseH2>

    <div class="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6">
      <NuxtLink
        v-for="card in tipCards"
        :key="card.to"
        :to="card.to"
        class="block"
      >
        <UCard
          variant="subtle"
          class="rounded-xl hover:bg-elevated transition-colors h-full"
        >
          <div class="flex items-center gap-3">
            <UIcon
              :name="card.icon"
              class="size-5 text-muted flex-shrink-0"
            />
            <div>
              <p class="font-medium text-sm text-highlighted">
                {{ card.title }}
              </p>
              <p class="text-xs text-muted">
                {{ card.description }}
              </p>
            </div>
          </div>
        </UCard>
      </NuxtLink>
    </div>

    <DataSourceAttribution />

    <DevOnly>
      <details>
        <summary>Details</summary>
        <pre>
          {{ broker }}
        </pre>
      </details>
    </DevOnly>
  </div>
</template>
